import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { AuthError, googleSignInUrl, login, requestReset, signup, updatePassword } from './auth-api';
import { recoveryToken, safeNext, type AuthPath } from './auth-paths';
import '../design-v2/ui';
import './auth-design-preview.css';
import './auth.css';

type Notice = { kind: 'error' | 'info'; text: string } | null;

function Screen({ title, subtitle, notice, children }: { title: string; subtitle: string; notice: Notice; children: ReactNode }) {
  useEffect(() => { document.title = `BnB Nodes — холст нейросетей: цепочка моделей одной кнопкой · ${title}`; }, [title]);
  const signIn = title === 'Sign in';
  return (
    <div className="auth-v2 nodex-v2" data-theme="light">
      <main className="auth-v2-main">
        <header className="auth-v2-header">
          <a className="auth-v2-logo" href="/#/" aria-label="Bots and Bones home"><img src="/assets/bnb-logo.png" alt="Bots and Bones" /></a>
          <div>{signIn ? <>New here? <a href="/signup">Create an account →</a></> : <>Have an account? <a href="/login">Sign in →</a></>}</div>
        </header>
        <div className="auth-v2-center"><section className="auth-v2-form-wrap">
          <div className="auth-v2-copy"><span>{signIn ? '[ Sign in ]' : `[ ${title} ]`}</span><h1>{signIn ? 'Welcome back' : title}</h1><p>{subtitle}</p></div>
          {notice && <div className="auth-v2-error" role={notice.kind === 'error' ? 'alert' : 'status'}><i />{notice.text}</div>}
          {children}
        </section></div>
        <footer className="auth-v2-legal"><a href="#terms">Terms</a><i>·</i><a href="#privacy">Privacy</a><i>·</i><a href="#help">Help</a></footer>
      </main>
      <AuthIllustration />
    </div>
  );
}

function AuthIllustration() {
  return <aside className="auth-v2-aside" aria-hidden="true"><div className="auth-v2-diagram">
    <div className="auth-v2-node node-prompt"><small>PROMPT <b>✓</b></small><span>ceramic cup, soft studio light, hero still for the spring launch</span></div>
    <div className="auth-v2-node node-ref"><small>REFERENCE <b>✓</b></small><span>cup-ref.jpg · 1536×2048</span></div>
    <div className="auth-v2-node node-model"><small>Flux Pro <i>62%</i></small><span>aspect <b>3:4</b></span><span>steps <b>28</b></span><div /><small>62% · 18 bxb</small></div>
    <svg viewBox="0 0 560 320"><path d="M240 60 C 280 60, 280 150, 320 150"/><path d="M240 260 C 280 260, 280 176, 320 176"/></svg>
    <div className="auth-v2-diagram-caption"><span>[ Bots and Bones ]</span><strong>Chain models into flows. Run them. Ship the outputs.</strong></div>
  </div></aside>;
}

function useSubmit(action: (form: FormData) => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [notice, setNotice] = useState<Notice>(null);
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true); setNotice(null);
    try { await action(new FormData(event.currentTarget)); }
    catch (error) { setNotice({ kind: 'error', text: error instanceof AuthError ? error.message : 'Something went wrong. Try again.' }); }
    finally { submitting.current = false; setBusy(false); }
  };
  return { busy, notice, setNotice, onSubmit };
}

const text = (form: FormData, name: string) => String(form.get(name) ?? '').trim();

function GoogleButton({ href, disabled }: { href: string; disabled?: boolean }) {
  return (
    <div className="auth-v2-oauth"><button type="button" disabled={disabled} onClick={() => location.assign(href)}><b aria-hidden="true">G</b>Continue with Google</button><div><span>or</span></div></div>
  );
}

function Login() {
  const next = safeNext(new URLSearchParams(location.search).get('next'));
  const { busy, notice, onSubmit } = useSubmit(async form => {
    await login(text(form, 'email'), String(form.get('password') ?? ''));
    location.replace(next);
  });
  const googleFailed: Notice = new URLSearchParams(location.search).get('error') === 'google'
    ? { kind: 'error', text: 'Google sign-in failed. Try again.' } : null;
  return (
    <Screen title="Sign in" subtitle="Pick up your flows where you left them." notice={notice ?? googleFailed}>
      <GoogleButton href={googleSignInUrl(next)} disabled={busy} />
      <form className="auth-v2-form" onSubmit={onSubmit} aria-busy={busy}>
        <label className="auth-v2-field" htmlFor="email"><span>Email</span><span className="auth-v2-input-wrap"><input id="email" name="email" type="email" autoComplete="email" placeholder="you@studio.com" required /></span></label>
        <label className="auth-v2-field" htmlFor="password"><span className="auth-v2-field-title"><span id="password-label">Password</span><a href="/reset">Forgot password?</a></span><span className="auth-v2-input-wrap"><input id="password" aria-labelledby="password-label" name="password" type="password" autoComplete="current-password" placeholder="Your password" required /></span></label>
        <button className="auth-v2-submit" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
    </Screen>
  );
}

function Signup() {
  const { busy, notice, setNotice, onSubmit } = useSubmit(async form => {
    const password = String(form.get('password') ?? '');
    if (password !== String(form.get('confirmPassword') ?? '')) {
      throw new AuthError('PASSWORD_MISMATCH', 'Passwords do not match. Please try again.');
    }
    const result = await signup(text(form, 'email'), password);
    setNotice({ kind: 'info', text: result.confirmationRequired ? 'Check your email to confirm your account.' : 'Account created. You can sign in now.' });
  });
  return (
    <Screen title="Create account" subtitle="One workspace for every flow, asset and run." notice={notice}>
      <form className="auth-v2-form" onSubmit={onSubmit} aria-busy={busy}>
        <label className="auth-v2-field" htmlFor="email"><span>Work email</span><span className="auth-v2-input-wrap"><input id="email" name="email" type="email" autoComplete="email" required /></span></label>
        <label className="auth-v2-field" htmlFor="password"><span id="password-label">Password</span><span className="auth-v2-input-wrap"><input id="password" aria-labelledby="password-label" aria-describedby="password-hint" name="password" type="password" autoComplete="new-password" minLength={8} required /></span><small id="password-hint">At least 8 characters.</small></label>
        <label className="auth-v2-field" htmlFor="confirm-password"><span>Confirm password</span><span className="auth-v2-input-wrap"><input id="confirm-password" name="confirmPassword" type="password" autoComplete="new-password" minLength={8} required /></span></label>
        <button className="auth-v2-submit" type="submit" disabled={busy}>{busy ? 'Creating account…' : 'Create account'}</button>
      </form>
      <div className="auth-links"><a href="/login">Sign in</a><a href="/reset">Forgot password?</a></div>
    </Screen>
  );
}

function Reset() {
  const { busy, notice, setNotice, onSubmit } = useSubmit(async form => {
    await requestReset(text(form, 'email'));
    setNotice({ kind: 'info', text: 'If that address has an account, we have sent a reset link.' });
  });
  return (
    <Screen title="Reset password" subtitle="Enter the email you signed up with. A reset link will arrive in a minute." notice={notice}>
      <form className="auth-v2-form" onSubmit={onSubmit} aria-busy={busy}>
        <label className="auth-v2-field" htmlFor="email"><span>Email</span><span className="auth-v2-input-wrap"><input id="email" name="email" type="email" autoComplete="email" required /></span></label>
        <button className="auth-v2-submit" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send reset link'}</button>
      </form>
    </Screen>
  );
}

function ResetPassword() {
  const [token] = useState(() => recoveryToken(location.hash));
  const [done, setDone] = useState(false);
  const { busy, notice, setNotice, onSubmit } = useSubmit(async form => {
    if (!token) throw new AuthError('INVALID_RECOVERY_LINK', 'This link is invalid or has expired. Request a new one.');
    const password = String(form.get('password') ?? '');
    if (password !== String(form.get('confirmPassword') ?? '')) {
      throw new AuthError('PASSWORD_MISMATCH', 'Passwords do not match. Please try again.');
    }
    await updatePassword(token, password);
    setDone(true);
    setNotice({ kind: 'info', text: 'Password changed. You can sign in now.' });
  });
  const invalid: Notice = token ? notice : { kind: 'error', text: 'This link is invalid or has expired. Request a new one.' };
  return (
    <Screen title="Choose a new password" subtitle="Pick something you haven’t used here before." notice={invalid}>
      {token && !done && (
        <form className="auth-v2-form" onSubmit={onSubmit} aria-busy={busy}>
          <label className="auth-v2-field" htmlFor="password"><span id="password-label">New password</span><span className="auth-v2-input-wrap"><input id="password" aria-labelledby="password-label" aria-describedby="password-hint" name="password" type="password" autoComplete="new-password" minLength={8} required autoFocus /></span><small id="password-hint">At least 8 characters.</small></label>
          <label className="auth-v2-field" htmlFor="confirm-password"><span>Repeat password</span><span className="auth-v2-input-wrap"><input id="confirm-password" name="confirmPassword" type="password" autoComplete="new-password" minLength={8} required /></span></label>
          <button className="auth-v2-submit" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save password'}</button>
        </form>
      )}
      <a className="auth-v2-footer-link" href={token && done ? '/login' : '/reset'}>{token && done ? 'Continue to sign in →' : 'Request a new reset link →'}</a>
    </Screen>
  );
}

export function AuthApp({ path }: { path: AuthPath }) {
  if (path === '/signup') return <Signup />;
  if (path === '/reset') return <Reset />;
  if (path === '/reset-password') return <ResetPassword />;
  return <Login />;
}
